import clsx from "clsx";
import { startCase } from "lodash";
import { FC, ReactElement, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { ErrorsDisplayContainer } from "@ryde/controls";
import {
  useCrudEditContext,
  usePassengerEdit,
  useShiftEdit,
} from "@ryde/redux";
import {
  archiveRelationErrorAtomFamily,
  canResolveAlways,
  EditFormMode,
  EntitiesWithoutDuplicateCrudUi,
  errorsCrudContentAtomFamily,
  formTabControlsSF,
  IEditFormDrawerProps,
  IExtendableDto,
  IInjectedDrawerProps,
  isInnerItemEditingAtom,
  RIGHTBAR_DEFAULT_WIDTH,
  RIGHTBAR_ROLES_WIDTH,
  RydeUiUnit,
  useHasPermissionCustomField,
  useHasPermissions,
} from "@ryde/shared";

import { FormControlsWrapper, useEditFormInner } from "../../../layout-edit";
import { EditFormTabs } from "../../../layout-edit/edit-form.tabs";
import {
  BaseDrawer,
  ContentBlurOverlay,
  CrudEditFormFooter,
  DrawerContentStyled,
  DrawerFormContentGate,
  DrawerHeader,
  FormFooter,
} from "../drawer";
import { RelatedEntitiesErrorButton } from "../related-entities";
import { useEditFormDrawerLoading } from "./use-edit-form-drawer-loading";

const getDrawerWidthByEntity = (entity: RydeUiUnit): string => {
  switch (entity) {
    case "role":
      return RIGHTBAR_ROLES_WIDTH;
    default:
      return RIGHTBAR_DEFAULT_WIDTH;
  }
};

export const DrawerEditFormInner: FC<
  IEditFormDrawerProps & IInjectedDrawerProps
> = ({
  onCloseDrawer,
  resolveFn,
  drawerNumber,
  subTitle,
  mode,
  canResolve = canResolveAlways,
  customWidth,
}) => {
  const {
    data: { isEdit, isDuplicate, dto },
    entity,
    id,
  } = useCrudEditContext<IExtendableDto>();

  const {
    onDuplicateClick,
    onArchiveClick,
    closeSidebar: onCloseHandler,
    onSaveClick: onDefaultSaveClick,
    closeSidebarOk,
  } = useEditFormInner({ resolveFn, canResolve, onCloseDrawer });
  const { getFieldPermissions } = useHasPermissionCustomField();
  const isAddNew = !isEdit;

  // Shifts and passengers own their save flow (validate -> confirm cascading to future bookings ->
  // save) via dedicated hooks, so the generic saveCrudEntity stays free of entity-specific
  // async-dialog logic.
  const { saveShift } = useShiftEdit<IExtendableDto>();
  const { savePassenger } = usePassengerEdit<IExtendableDto>();
  const onShiftSaveClick = useEvent(async () => {
    const saveResult = await saveShift(dto);
    if (saveResult?.success) {
      closeSidebarOk(saveResult.data);
    }
  });
  const onPassengerSaveClick = useEvent(async () => {
    const saveResult = await savePassenger(dto);
    if (saveResult?.success) {
      closeSidebarOk(saveResult.data);
    }
  });

  const entitySaveHandlers: Partial<Record<string, () => Promise<void>>> = {
    shift: onShiftSaveClick,
    passenger: onPassengerSaveClick,
  };
  const onSaveClick = entitySaveHandlers[entity] ?? onDefaultSaveClick;

  const {
    formState: { isDirty, isValid, errors },
  } = useFormContext();
  const { t } = useTranslation();
  const { isFormContentLoading, isFormFooterLoading } =
    useEditFormDrawerLoading({
      entity,
      requestedId: id,
      dtoId: dto?.id,
      hasDto: Boolean(dto),
    });
  const serverErrors = useRecoilValue(errorsCrudContentAtomFamily(entity));
  const hasArchiveRelationError = useRecoilValue(
    archiveRelationErrorAtomFamily(entity),
  );
  const currentTabControls = useRecoilValue(formTabControlsSF(entity));
  const isInnerItemEditing = useRecoilValue(isInnerItemEditingAtom);

  const unitTitle = t(`${entity}.single`);
  const addTitle = t(`general.ADD_NEW`, { entity: unitTitle });

  // Generate title: "Entity: Name" for edit, "Add new Entity" for adding (startCase like other drawers).
  // Only the entity label is startCased — the user-provided name is rendered raw and bidi-isolated
  // (dir="auto") so mixed RTL/LTR names (e.g. "משמרת ב 19:30") keep their original characters and order.
  let title: string | ReactElement;
  if (isEdit) {
    if (dto) {
      const entityName = dto.title || dto.name || "";
      title = entityName ? (
        <>
          {startCase(unitTitle)}: <span dir="auto">{entityName}</span>
        </>
      ) : (
        startCase(unitTitle)
      );
    } else {
      title = startCase(unitTitle);
    }
  } else {
    title = startCase(addTitle);
  }

  title = subTitle ? (
    <>
      {title} <span className="sub_header">{subTitle}</span>
    </>
  ) : (
    title
  );
  const { hasPermissions: hasUpdatePermissions } = useHasPermissions({
    entity,
    action: "update",
  });

  const hasSomeCustomFieldWithUpdatePermission = useMemo(
    () =>
      currentTabControls.some(
        ctrl =>
          !getFieldPermissions({
            ...ctrl,
            formEntity: entity,
          }).isFieldDisabled,
      ),
    [currentTabControls, entity, getFieldPermissions],
  );

  const isSaveDisabled =
    isInnerItemEditing ||
    !isValid ||
    (!isDirty && !isDuplicate) ||
    (isEdit &&
      !hasUpdatePermissions &&
      !hasSomeCustomFieldWithUpdatePermission);

  const isDuplicateButtonVisible =
    isEdit && !EntitiesWithoutDuplicateCrudUi.includes(entity);

  const drawerWidth =
    customWidth !== undefined ? customWidth : getDrawerWidthByEntity(entity);

  useEffect(() => {
    if (isInnerItemEditing) {
      document.body.classList.add("inner-item-editing-active");
    } else {
      document.body.classList.remove("inner-item-editing-active");
    }
    return () => {
      document.body.classList.remove("inner-item-editing-active");
    };
  }, [isInnerItemEditing]);

  return (
    <BaseDrawer
      onCloseDrawer={onCloseHandler}
      drawerNumber={drawerNumber || 0}
      customWidth={drawerWidth}
    >
      <DrawerFormContentGate
        isLoading={isFormContentLoading}
        entityTitle={unitTitle}
      >
        <DrawerHeader
          onCloseDrawer={onCloseHandler}
          title={title}
          drawerNumber={drawerNumber}
        />
        <DrawerContentStyled
          className={clsx(
            "drawer-form-with-image",
            isInnerItemEditing && "inner-item-editing-blur",
          )}
        >
          <ContentBlurOverlay
            className={clsx(isInnerItemEditing && "active")}
          />
          <EditFormTabs>
            <ErrorsDisplayContainer
              serverErrors={serverErrors}
              formErrors={isValid ? undefined : errors}
            />
            {hasArchiveRelationError && isEdit && (
              <RelatedEntitiesErrorButton
                entity={entity}
                entityId={dto?.id}
                entityLabel={dto?.title || dto?.name}
              />
            )}
            {(isAddNew || !!dto) && (
              <FormControlsWrapper controls={currentTabControls} />
            )}
          </EditFormTabs>
        </DrawerContentStyled>
        {mode !== EditFormMode.CrudRoute && (
          <FormFooter
            entity={entity}
            onSaveClick={onSaveClick}
            onCancelClick={onCloseHandler}
            isLoading={isFormFooterLoading}
            isSaveDisabled={isSaveDisabled}
            isInnerItemEditing={isInnerItemEditing}
          />
        )}
        {mode === EditFormMode.CrudRoute && (
          <CrudEditFormFooter
            entity={entity}
            onSaveClick={onSaveClick}
            onCancelClick={onCloseHandler}
            isLoading={isFormFooterLoading}
            isSaveDisabled={isSaveDisabled}
            isInnerItemEditing={isInnerItemEditing}
            duplicateClick={
              isDuplicateButtonVisible ? onDuplicateClick : undefined
            }
            isDuplicateDisabled={isAddNew}
            archiveClick={isEdit ? onArchiveClick : undefined}
            isArchiveDisabled={isAddNew}
            deleteMessage={t(`general.ARCHIVE_MESSAGE`, {
              entity: unitTitle,
            })}
            duplicationMessage={t(`general.DUPLICATE_MESSAGE`, {
              entity: unitTitle,
            })}
          />
        )}
      </DrawerFormContentGate>
    </BaseDrawer>
  );
};
